import {
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import { InvoiceType } from 'common/enums/invoiceType'
import { getInvoicingSystemName, isApiSystem } from 'common/invoicing-systems/invoicing-system-helper'
import Spinner from 'common/spinner'
import { InvoicesSendResult } from 'models/invoices-send-result'
import { User } from 'models/user'
import { BillingDialog } from 'pages/contract-page/billing-dialog'
import React from 'react'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/app/app-store'
import {
    TEXT_ACTIVITY_BASED_INVOICES,
    TEXT_ALL_INVOICE_TYPES,
    TEXT_DO_YOU_WANT_TO_CONTINUE,
    TEXT_GROUPING_TYPE_BY_PRODUCT,
    TEXT_GROUP_BY_BUILDING_NUMBER,
    TEXT_GROUP_BY_MATRIKKEL_NUMBER,
    TEXT_INVOICE_TYPE,
    TEXT_NEW_INVOICES_CREATED_BY_USER,
    TEXT_PERIODIC_COST_TYPE_NORMAL,
    TEXT_PERIODIC_COST_TYPE_RUNNING,
    TEXT_PERIODIC_INVOICES,
    TEXT_SEND_TO,
    TEXT_SEND_TO_ERP,
    TEXT_YES_SEND_TO,
    TEXT_YOU_ARE_ABOUT_TO_SEND_INVOICES,
} from 'utils/translations/keys'
import { tr } from 'utils/translations/translate'
import {
    ACTIVITY_BASED_INVOICE_TYPE,
    ALL_INVOICE_TYPES,
    GROUPING_TYPE_BY_PRODUCT,
    GROUPING_TYPE_NONE,
    PERIODIC_INVOICE_TYPE,
    PERIODIC_TYPE_NORMAL,
    PERIODIC_TYPE_RUNNING,
    USER_INVOICE_TYPE,
} from './dashboard-constants'

export const SendToErpDialog: React.FC<{
    invoicesSendResult: InvoicesSendResult
    sendToErpDialogOpen: boolean
    sendToErp: () => void
    styles: any
    inputNumberOfPaymentInvoicesToSend: string
    numberOfInvoicesToSend: number
    setInputNumberOfInvoicesToSend: (value: string) => void
    handleSendToErpDialogClose: () => void
    showKomtekSettings: boolean
    onInvoiceTypeChange: (value: unknown) => void
    invoiceType: InvoiceType
    handleChangeGroupingType: (value: string) => void
    sendToErpRunning: boolean
    user: User | undefined
    handleChangePeriodicType: (value: string) => void
    groupingType: number
    periodicCostType: number
    selectedPeriodForSending: any
}> = (props) => {
    const {
        invoicesSendResult,
        sendToErpDialogOpen,
        sendToErp,
        styles,
        inputNumberOfPaymentInvoicesToSend,
        numberOfInvoicesToSend,
        setInputNumberOfInvoicesToSend,
        handleSendToErpDialogClose,
        showKomtekSettings,
        onInvoiceTypeChange,
        invoiceType,
        handleChangeGroupingType,
        sendToErpRunning,
        user,
        handleChangePeriodicType,
        groupingType,
        periodicCostType,
        selectedPeriodForSending,
    } = props

    const processNotStarted = invoicesSendResult.successCount === 0 && invoicesSendResult.failureCount === 0
    const userInvoiceCount = selectedPeriodForSending?.userInvoicesNotTransferred ?? 0
    const oneOfInvoiceCount = selectedPeriodForSending?.oneOfInvoicesNotTransferred ?? 0
    const tenantConfig = useSelector((state: AppState) => state.config.tenantConfig)
    const invoicingSystemName = getInvoicingSystemName(tenantConfig?.invoiceSystem)
    const okText = isApiSystem(tenantConfig?.invoiceSystem) ? `${tr(TEXT_YES_SEND_TO)} ${invoicingSystemName}` : tr(TEXT_SEND_TO_ERP)
    const showForApiSystem = isApiSystem(tenantConfig?.invoiceSystem)
    const title = isApiSystem(tenantConfig?.invoiceSystem) ? `${tr(TEXT_SEND_TO)} ${invoicingSystemName}` : tr(TEXT_SEND_TO_ERP)

    return (
        <BillingDialog
            title={title}
            open={sendToErpDialogOpen}
            handleClose={handleSendToErpDialogClose}
            handleOk={sendToErp}
            okText={okText}
            showOk={!sendToErpRunning}
        >
            {invoicesSendResult && numberOfInvoicesToSend > 0 && processNotStarted && (
                <>
                {showForApiSystem &&
                    <div>
                        <p>{`${tr(TEXT_YOU_ARE_ABOUT_TO_SEND_INVOICES)} ${invoicingSystemName}.`}</p>
                        <p>{tr(TEXT_DO_YOU_WANT_TO_CONTINUE)}</p>
                    </div>}
                    <div>
                        <Grid container spacing={2} className={styles.sendToErpContainer}>
                            {user?.godMode && (
                                <Grid item xs={12}>
                                    <TextField
                                        label="Invoice count"
                                        type="number"
                                        InputProps={{ inputProps: { min: 0, style: { textAlign: 'right' } } }}
                                        value={inputNumberOfPaymentInvoicesToSend}
                                        onChange={(item) => {
                                            setInputNumberOfInvoicesToSend(item.target.value)
                                        }}
                                    ></TextField>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <InputLabel id="select-invoice-type-input-label">{tr(TEXT_INVOICE_TYPE)}</InputLabel>
                                <Select
                                    labelId="select-invoice-type-input-label"
                                    onChange={(e) => onInvoiceTypeChange(e.target.value)}
                                    value={invoiceType}
                                >
                                    <MenuItem key={0} value={ALL_INVOICE_TYPES}>
                                        {tr(TEXT_ALL_INVOICE_TYPES)}
                                    </MenuItem>
                                    <MenuItem key={1} value={PERIODIC_INVOICE_TYPE}>
                                        {tr(TEXT_PERIODIC_INVOICES)}
                                    </MenuItem>
                                    <MenuItem key={2} value={ACTIVITY_BASED_INVOICE_TYPE}>
                                        {`${tr(TEXT_ACTIVITY_BASED_INVOICES)} (${oneOfInvoiceCount})`}
                                    </MenuItem>
                                    <MenuItem key={3} value={USER_INVOICE_TYPE}>
                                        {`${tr(TEXT_NEW_INVOICES_CREATED_BY_USER)} (${userInvoiceCount})`}
                                    </MenuItem>
                                </Select>
                            </Grid>
                            {showKomtekSettings && invoiceType === PERIODIC_INVOICE_TYPE && (
                                <>
                                    <Grid item xs={12}>
                                        <RadioGroup
                                            aria-label="periodic type"
                                            name="periodicType"
                                            onChange={(e) => handleChangePeriodicType(e.target.value)}
                                            value={periodicCostType}
                                        >
                                            <FormControlLabel
                                                value={PERIODIC_TYPE_RUNNING}
                                                control={<Radio />}
                                                label={tr(TEXT_PERIODIC_COST_TYPE_RUNNING)}
                                            />
                                            <FormControlLabel
                                                value={PERIODIC_TYPE_NORMAL}
                                                control={<Radio />}
                                                label={tr(TEXT_PERIODIC_COST_TYPE_NORMAL)}
                                            />
                                        </RadioGroup>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography>{tr(TEXT_GROUPING_TYPE_BY_PRODUCT)}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <RadioGroup
                                            aria-label="grouping type"
                                            name="groupingType"
                                            onChange={(e) => handleChangeGroupingType(e.target.value)}
                                            value={groupingType}
                                        >
                                            <FormControlLabel
                                                value={GROUPING_TYPE_BY_PRODUCT}
                                                control={<Radio />}
                                                label={tr(TEXT_GROUP_BY_MATRIKKEL_NUMBER)}
                                            />
                                            <FormControlLabel
                                                value={GROUPING_TYPE_NONE}
                                                control={<Radio />}
                                                label={tr(TEXT_GROUP_BY_BUILDING_NUMBER)}
                                            />
                                        </RadioGroup>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </div>
                </>
            )}
            {sendToErpRunning && <Spinner />}
        </BillingDialog>
    )
}
